import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../firebase/AuthProvider';
import { DEFAULT_PHASES } from '../constants/masterData';
import { ep } from '../api/endpoints';

export interface ProjectFilters {
  project_name?: string;
  phase?: string[];
  main_staff_name?: string;
  main_staff_branch_name?: string[];
  main_staff_department_name?: string[];
  client_name?: string;
  category_large?: string;
  category_medium?: string;
  project_type?: string[];
  remarks?: string;
  remarks_exclude?: string;
  long_term_follow_up?: number;
  show_lost?: boolean;
  show_long_term?: boolean;
  show_completed?: boolean;
  [key: string]: any;
}

export const DEFAULT_FILTERS: ProjectFilters = {
  project_name: '',
  phase: [...DEFAULT_PHASES],
  main_staff_name: '',
  main_staff_branch_name: [],
  main_staff_department_name: [],
  client_name: '',
  category_large: '',
  category_medium: '',
  project_type: [],
  remarks: '',
  remarks_exclude: '',
  show_long_term: false,
};

export interface SortConfig {
  field: string;
  order: 'asc' | 'desc';
}

const FUNCTION_URL = ep('get_salesforce_data');

export const useProjectTable = (
  viewLevel: string, 
  permissions: string[] | null,
  initialFilters: ProjectFilters = DEFAULT_FILTERS,
  initialSort: SortConfig = { field: 'phase', order: 'asc' }
) => {
  const { user } = useAuth();
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<ProjectFilters>(initialFilters);
  const [sort, setSort] = useState<SortConfig>(initialSort);

  const fetchData = useCallback(async () => {
    if (permissions === null || !user?.email) return;

    setLoading(true);
    try {
      const response = await axios.post(FUNCTION_URL, {
        page,
        pageSize,
        filters,
        sortField: sort.field,
        sortOrder: sort.order,
        userEmail: user.email,
        viewLevel
      });

      if (response.data.status === 'success') {
        setData(response.data.rowsResult || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, sort, user?.email, viewLevel, permissions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFilters = useCallback((newFilters: ProjectFilters) => {
    // 保存済み条件を適用する場合などは、不完全なオブジェクトが渡される可能性があるため
    // デフォルト値とマージすることで、以前の値を確実にクリアする
    const normalizedFilters = { ...newFilters };

    // 互換性維持: 以前の単一選択（文字列）の条件が渡された場合に配列へ変換
    if (normalizedFilters.main_staff_branch_name && !Array.isArray(normalizedFilters.main_staff_branch_name)) {
      normalizedFilters.main_staff_branch_name = [normalizedFilters.main_staff_branch_name as any];
    }
    if (normalizedFilters.main_staff_department_name && !Array.isArray(normalizedFilters.main_staff_department_name)) {
      normalizedFilters.main_staff_department_name = [normalizedFilters.main_staff_department_name as any];
    }

    setFilters({ ...DEFAULT_FILTERS, ...normalizedFilters });
    setPage(0); // フィルタ変更時は1ページ目に戻す
  }, []);

  const handleSort = useCallback((field: string) => {
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
    setPage(0);
  }, []);

  const updateSort = useCallback((newSort: SortConfig) => {
    setSort(newSort);
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    sort,
    setPage,
    setPageSize,
    updateFilters,
    handleSort,
    updateSort,
    refresh: fetchData
  };
};
